import React from "react";
import { GenericTag } from "../Tag/GenericTag";
import GenericTooltip from "../Tooltip/GenericTooltip";

export interface CustomRadioOption {
  value: string;
  label: string;
  description?: string;
  bodyContent?: React.ReactNode;
  disabled?: boolean;
  tooltipText?: string;
  /** Tag shown next to the label when `disabled` is true. Defaults to "Coming Soon". */
  disabledTagLabel?: string;
}

interface CustomRadioGroupProps {
  name: string;
  options: CustomRadioOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  direction?: "row" | "col";
}

const CustomRadioGroup: React.FC<CustomRadioGroupProps> = ({
  name,
  options,
  value,
  onChange,
  className = "",
  direction = "col",
}) => (
  <div
    className={`flex ${
      direction === "row" ? "flex-row" : "flex-col"
    } gap-6 ${className}`}
  >
    {options.map((option) => (
      <label 
        key={option.value} 
        className={`flex gap-3 items-start select-none ${option.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
      >
        <span className="relative flex items-center h-6 w-6 mt-1 flex-shrink-0">
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={() => !option.disabled && onChange(option.value)}
            disabled={option.disabled}
            className="peer appearance-none w-6 h-6 border-2 border-[var(--color-semantic-border-secondary,#DCDDE0)] rounded-full transition-colors checked:border-[var(--color-semantic-bg-brand-bold,#197278)] disabled:opacity-50 disabled:cursor-not-allowed"
          />
          {/* Outer ring */}
          <span className={`
            pointer-events-none absolute left-0 top-0 w-6 h-6 rounded-full
            border-2
            ${value === option.value
              ? 'border-[var(--color-semantic-bg-brand-bold,#197278)]'
              : 'border-[var(--color-semantic-border-secondary,#DCDDE0)]'
            }
          `}></span>
          {/* Inner dot */}
          {value === option.value && (
            <span className="pointer-events-none absolute left-1/2 top-1/2 w-3.5 h-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--color-semantic-bg-brand-bold,#197278)]"></span>
          )}
        </span>
        <div className="flex flex-col items-start w-full">
          <div className="flex items-center gap-2">
            <div className="font-semibold text-body text-semantic-text-primary leading-6">{option.label}</div>
            {option.tooltipText && (
              <GenericTooltip text={option.tooltipText} position="top" width="20rem" />
            )}
            {option.disabled && (
              <GenericTag label={option.disabledTagLabel ?? "Coming Soon"} variant="neutral" size="small" />
            )}
          </div>
          {option.description && (
            <div className="text-sm text-semantic-text-secondary mt-1">{option.description}</div>
          )}
          {option.bodyContent && value === option.value && (
            <div className="w-full -ml-9 mt-3">
              {option.bodyContent}
            </div>
          )}
        </div>
      </label>
    ))}
  </div>
);

export default CustomRadioGroup; 